import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import common from 'constants/common';
import { scrollMessagesToBottom } from 'components/molecules/smsDashboard/messagesList';
import ChatInput from 'components/atoms/smsDashboard/input';
import useCacheString from 'utils/useCacheString';
import BaseSelect from 'components/atoms/baseSelect';
import BaseButton from 'components/atoms/baseButton';
import CopyImg from 'components/atoms/icons/copyOutlinedIcon';
import { useSendMessage } from 'infrastructure/hooks/smsDashboard/useSendMessage';
import SmsDashboardTemplate from 'components/molecules/smsDashboard/template';
import { CONVERSATION_TYPES } from 'store/smsDashboard';
import {
  convertDateToTimeZone,
  convertTimeZoneToUtc,
  DateFormatter,
  generateAvailabilityDatesNext2Week,
} from 'infrastructure/functions';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { useSendScheduledMessage } from 'infrastructure/hooks/smsDashboard/useSendScheduledMessage';
import { Availability } from 'infrastructure/classes/availability';
import { DateFormats } from 'infrastructure/enums';
import { useInterval } from 'react-use';

import ScheduledMessageModal from '../scheduledMessageModal';
import s from './styles.module.scss';

import type { RootDispatch, RootState } from 'store';
import type { IConversation } from 'infrastructure/interfaces';
import type { IContactAvailability } from 'infrastructure/hooks/interactiveTimeDashboard/types';

const chatInputId = 'smsDashboard.messagesInput';

const selectTheme = (theme: any) => ({
  ...theme,
  borderRadius: 12,
  colors: {
    ...theme.colors,
    ...common.primaryColors,
  },
});

interface IMessageInputProps {
  conversation: IConversation;
  availabilities: IContactAvailability[];
  timezone: string;
  refetch: () => void;
  disabled?: boolean;
  disableTemplate?: boolean;
}

const MessagesInput: React.FC<IMessageInputProps> = (props) => {
  const {
    conversation,
    timezone,
    availabilities,
    refetch,
    disabled = false,
    disableTemplate = false,
  } = props;

  const { t } = useTranslation();

  const patient = useSelector((state: RootState) => state.patient);

  const { currentConversation, conversationType } = useSelector(
    (state: RootState) => state.smsDashboard,
  );

  const { getDetails: patientDetailsLoading } = useSelector(
    (state: RootState) => state.loading.effects.patient,
  );

  const {
    smsDashboard: { changeCurrentConversation },
  } = useDispatch<RootDispatch>();

  const isUnregisteredConversationType =
    conversationType === CONVERSATION_TYPES.UNREGISTERED_RPM ||
    conversationType === CONVERSATION_TYPES.UNREGISTERED_CCM;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [currentPatientDate, setCurrentPatientDate] = useState(
    convertDateToTimeZone(new Date(), timezone),
  );

  const [isScheduledMessageModalOpen, setIsScheduledMessageModalOpen] =
    useState(false);

  const [phoneNumber, setPhoneNumber] = useState<{
    label: string;
    value: string | null;
  } | null>({ label: '', value: '' });

  const [inputValue, setInputValue] = useState<string>('');

  const sortedAvailabilities = useMemo(() => {
    if (!availabilities || availabilities.length === 0) return [];

    return [...availabilities].sort((a, b) => {
      if (a.day !== b.day) {
        return a.day - b.day;
      }
      return a.startTime.localeCompare(b.startTime);
    });
  }, [availabilities]);

  const isAvailable = useMemo(() => {
    if (!availabilities.length) return true;
    return Availability.isAvailable(
      availabilities.map(
        (el, i) =>
          new Availability({
            day: el.day,
            startTime: el.startTime,
            endTime: el.endTime,
            guid: `${i}`,
            patientGuid: `${i}`,
          }),
      ),
      timezone,
    );
  }, [availabilities, currentPatientDate, timezone]);

  const scheduledTime: string | null = useMemo(() => {
    if (sortedAvailabilities.length) {
      const nexA = generateAvailabilityDatesNext2Week(
        sortedAvailabilities.map((el) => ({
          day: el.day,
          startTime: el.startTime,
        })),
      );

      if (nexA.length) {
        const dateTime = DateFormatter({
          date: nexA[0],
          format: DateFormats["yyyy-MM-dd'T'HH:mm:ss"],
        });

        const utcDate = convertTimeZoneToUtc(dateTime, timezone);
        return utcDate.toISOString();
      }

      return null;
    }
    return null;
  }, [sortedAvailabilities, timezone]);

  const phoneOptions = useMemo(() => {
    if (patientDetailsLoading || !patient?.profile) return [];

    const { phone, homeNumber } = patient.profile;

    if (!homeNumber) return [{ label: phone, value: phone }];

    return [
      { label: phone, value: phone },
      { label: homeNumber, value: homeNumber },
    ];
  }, [patient, patientDetailsLoading]);

  const messagePlatformType = useMemo(() => {
    const moduleMap = {
      [CONVERSATION_TYPES.UNREGISTERED_RPM]: CONVERSATION_TYPES.RPM,
      [CONVERSATION_TYPES.UNREGISTERED_CCM]: CONVERSATION_TYPES.CCM,
      [CONVERSATION_TYPES.RPM]: CONVERSATION_TYPES.RPM,
      [CONVERSATION_TYPES.CCM]: CONVERSATION_TYPES.CCM,
    };
    return moduleMap[conversationType] || CONVERSATION_TYPES.RPM;
  }, [conversationType]);

  const { cacheLoading, deleteCache } = useCacheString({
    cacheId: `${chatInputId}.${conversation.id}`,
    value: inputValue,
    setValue: setInputValue,
  });

  const { sendMessage, sendLoading } = useSendMessage({
    onSuccess() {
      setInputValue('');
      scrollMessagesToBottom();
      deleteCache();
      changeCurrentConversation({ ...currentConversation, closed: false });
    },
  });
  const { sendScheduledMessage, sendScheduledMessageLoading } =
    useSendScheduledMessage(
      {},
      {
        onSuccess() {
          setInputValue('');
          setIsScheduledMessageModalOpen(false);
          scrollMessagesToBottom();
          notificationController.info({
            message: t(
              'smsDashboard.message_scheduled_for_closest_available_time',
            ),
          });
        },
      },
    );

  const sendMessageHandler = useCallback(
    (value: string) => {
      sendMessage({
        conversationId: conversation.id,
        message: value,
        from: messagePlatformType,
        patientPhone: phoneNumber?.value || conversation.patient.patientPhone,
        messageType: 'sms',
      });
    },
    [
      conversation.id,
      conversation.patient.patientPhone,
      messagePlatformType,
      phoneNumber?.value,
      sendMessage,
    ],
  );

  const sendScheduledMessageHandler = useCallback(
    (value: string) => {
      sendScheduledMessage({
        conversationId: conversation.id,
        message: value,
        from: messagePlatformType,
        patientPhone: phoneNumber?.value || conversation.patient.patientPhone,
        scheduledTime,
      });
    },
    [
      conversation.id,
      conversation.patient.patientPhone,
      messagePlatformType,
      phoneNumber?.value,
      scheduledTime,
      sendScheduledMessage,
    ],
  );

  const onSubmit = useCallback(
    async (value: string) => {
      if (!value) return;
      if (!isUnregisteredConversationType) {
        await refetch();
      }
      if (isAvailable) {
        sendMessageHandler(value);
      } else {
        setIsScheduledMessageModalOpen(true);
      }
    },
    [isAvailable, refetch, sendMessageHandler, isUnregisteredConversationType],
  );

  const modalSubmit = async (data: string) => {
    setIsModalOpen(false);
    setInputValue(data);
    await onSubmit(data);
  };

  useEffect(() => {
    setInputValue('');
    setTimeout(() => {
      document.getElementById(chatInputId)?.focus();
    }, 50);
  }, [currentConversation.id]);

  useInterval(
    () => {
      setCurrentPatientDate(convertDateToTimeZone(new Date(), timezone));
    },
    timezone ? 60_000 : null,
  );

  return (
    <div className={s['messages-input-wrapper']}>
      {false &&
        !patientDetailsLoading &&
        phoneOptions.length &&
        !isUnregisteredConversationType && (
          <div className={s['messages-input-phone']}>
            <p>{t('smsDashboard.phone_number_for_reply_to')}:</p>
            <BaseSelect
              options={phoneOptions}
              theme={(theme) => selectTheme(theme)}
              defaultValue={phoneOptions[0] || null}
              classNamePrefix="select-type"
              onChange={(value) => setPhoneNumber(value)}
            />
          </div>
        )}
      <div className={s['chat-form-wrapper']}>
        <BaseButton
          icon={<CopyImg />}
          type="secondary"
          className={s['message-template-button']}
          onClick={() => setIsModalOpen(true)}
          disabled={disableTemplate}
        />
        <ChatInput
          onSubmit={() => onSubmit(inputValue)}
          value={inputValue}
          setValue={setInputValue}
          loading={sendLoading || cacheLoading || sendScheduledMessageLoading}
          id={chatInputId}
          disabled={disabled}
        />
      </div>
      <SmsDashboardTemplate
        setIsOpen={setIsModalOpen}
        isOpen={isModalOpen}
        onSubmit={(data) => modalSubmit(data)}
        modalLabel={t('smsDashboard.sms_template')}
      />
      <ScheduledMessageModal
        isOpen={isScheduledMessageModalOpen}
        toggle={() =>
          setIsScheduledMessageModalOpen(!isScheduledMessageModalOpen)
        }
        sendNow={() => {
          sendMessageHandler(inputValue);
          setIsScheduledMessageModalOpen(false);
        }}
        sendLater={() => {
          sendScheduledMessageHandler(inputValue);
        }}
        sendLaterLoading={sendScheduledMessageLoading}
      />
    </div>
  );
};

export default MessagesInput;
